import { useState, useEffect, useCallback } from 'react';
import {
  Pane,
  Card,
  Badge,
  Text,
  Heading,
  Strong,
  Spinner,
  Button,
  ArrowRightIcon,
  CameraIcon,
  PlayIcon,
  TickIcon,
  TrashIcon,
  IconButton,
  Dialog,
  Image
} from 'evergreen-ui';
import api from '../../../services/api';

const getPercentageColor = (pct) => {
  if (pct >= 80) return '#059669';
  if (pct >= 50) return '#D97706';
  if (pct >= 25) return '#DC2626';
  return '#6B7280';
};

export const WebAppsTab = ({
  webapps,
  isLoading,
  hasMore,
  searchQuery,
  actionLoading,
  handleAction,
  openEntityOverview,
  currentProject
}) => {
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [wstgPercentages, setWstgPercentages] = useState({});
  const [expandedTech, setExpandedTech] = useState({});

  const fetchWstgPercentages = useCallback(async () => {
    if (!currentProject?.id || !webapps.length) return;
    const domainIds = webapps.filter(a => a.type === 'domain').map(a => a.id);
    const hostIds = webapps.filter(a => a.type === 'host').map(a => a.id);
    if (!domainIds.length && !hostIds.length) return;
    try {
      const params = {};
      if (domainIds.length) params.domain_ids = domainIds.join(',');
      if (hostIds.length) params.host_ids = hostIds.join(',');
      const response = await api.get(
        `/projects/${currentProject.id}/wstg-checklist/percentages`,
        { params }
      );
      setWstgPercentages(response.data.percentages || {});
    } catch (err) {
      // Silently ignore — WSTG data is optional
    }
  }, [currentProject?.id, webapps]);

  useEffect(() => {
    fetchWstgPercentages();
  }, [fetchWstgPercentages]);

  const cleanupTech = async () => {
    if (!currentProject?.id) return;
    try {
      const res = await api.post(`/projects/${currentProject.id}/results/cleanup-tech`);
      alert(`Очищено ${res.data.cleaned} записей. Перезапусти сканы чтобы заново заполнить технологии.`);
    } catch (err) {
      alert('Ошибка при очистке');
    }
  };

  return (
    <Pane>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={600}>
          Web Applications {webapps.length > 0 && `(${webapps.length} shown${!searchQuery && hasMore ? ', more available' : ''})`}
        </Heading>
        <Button size="small" appearance="minimal" intent="danger" onClick={cleanupTech}>
          Fix tech data
        </Button>
      </Pane>
      
      {webapps.length === 0 && !isLoading ? (
        <Card background="tint1" padding={32} textAlign="center">
          <Text color="muted">
            {searchQuery ? 'No web applications match your search' : 'No web applications found'}
          </Text>
        </Card>
      ) : (
        <Pane>
          {webapps.map(app => (
            <Card
              key={app.id}
              background={app.is_new ? "#F0FDF4" : (app.is_checked ? "#F9FAFB" : "white")}
              border={app.is_new ? "2px solid #10B981" : "1px solid #E5E7EB"}
              padding={16}
              marginBottom={8}
              elevation={0}
            >
              <Pane display="flex" alignItems="flex-start" justifyContent="space-between">
                <Pane flex={1}>
                  <Pane display="flex" alignItems="center" marginBottom={8} flexWrap="wrap">
                    <Pane display="flex" alignItems="center" flexWrap="wrap" width="100%">
                      <Pane display="flex" alignItems="center" flexWrap="wrap" flex={1}>
                        <Strong
                          size={500}
                          marginRight={8}
                          color={app.is_new ? "#047857" : (app.is_checked ? "#374151" : "#111827")}
                          style={{ fontWeight: 600 }}
                        >
                          {app.type === 'domain' ? app.name : (app.type === 'host' ? app.ip_address : app.url)}
                        </Strong>
                        <a
                          href={app.type === 'domain' ? `https://${app.name}` : (app.type === 'host' ? `http://${app.ip_address}` : app.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginRight: 8, fontSize: 13, color: '#3366CC', textDecoration: 'none', whiteSpace: 'nowrap' }}
                        >
                          ↗
                        </a>
                        <Badge
                          backgroundColor={app.type === 'domain' ? '#EFF6FF' : (app.type === 'host' ? '#ECFDF5' : '#FFFBEB')}
                          color={app.type === 'domain' ? '#1E3A8A' : (app.type === 'host' ? '#047857' : '#78350F')}
                          marginRight={8}
                          style={{ fontWeight: 500 }}
                        >
                          {app.type.toUpperCase()}
                        </Badge>
                        {app.is_new && (
                          <Badge backgroundColor="#10B981" color="white" marginRight={8} style={{ fontWeight: 500 }}>
                            NEW
                          </Badge>
                        )}
                        {app.is_checked && (
                          <Badge backgroundColor="#6B7280" color="white" style={{ fontWeight: 500 }}>
                            CHECKED
                          </Badge>
                        )}
                      </Pane>
                      {/* WSTG Percentage - only for domain and host types, shown under the name */}
                      {(app.type === 'domain' || app.type === 'host') && wstgPercentages[app.id] !== undefined && (
                        <Pane width="100%" marginTop={4}>
                          <Text
                            size={400}
                            color={getPercentageColor(wstgPercentages[app.id])}
                            marginTop={4}
                            fontWeight={700}
                            style={{ fontSize: '16px', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                          >
                            {wstgPercentages[app.id].toFixed(1)}%
                          </Text>
                        </Pane>
                      )}
                    </Pane>
                  </Pane>
                  
                  {app.type === 'url' && app.content_length && (
                    <Pane marginBottom={4}>
                      <Badge backgroundColor="#F7F8FA" color="default">
                        Size: {app.content_length} bytes
                      </Badge>
                    </Pane>
                  )}
                  {(() => {
                    const techs = (app.technology_detected || []).filter(t => t && t.length > 1);
                    if (!techs.length) return null;
                    return (
                      <Pane marginBottom={4}>
                        <Badge
                          backgroundColor="#E6F3FF"
                          color="blue"
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => setExpandedTech(prev => ({ ...prev, [app.id]: !prev[app.id] }))}
                        >
                          {expandedTech[app.id] ? '▾' : '▸'} {techs.length} {techs.length === 1 ? 'technology' : 'technologies'}
                        </Badge>
                        {expandedTech[app.id] && (
                          <Pane marginTop={6} display="flex" flexWrap="wrap" gap={4}>
                            {techs.map((tech, i) => (
                              <Badge key={i} backgroundColor="#F0F4FF" color="blue">
                                {tech}
                              </Badge>
                            ))}
                          </Pane>
                        )}
                      </Pane>
                    );
                  })()}
                  
                  {(app.type === 'domain' || app.type === 'host') && app.url_count > 0 && (
                    <Text 
                      size={300} 
                      color={app.is_new ? "#047857" : (app.is_checked ? "#4B5563" : "#6B7280")} 
                      marginBottom={8}
                      style={{ fontWeight: 400 }}
                    >
                      {app.url_count} URLs found
                    </Text>
                  )}
                  
                  {app.type === 'host' && app.domains && app.domains.length > 0 && (
                    <Text 
                      size={300} 
                      color={app.is_new ? "#047857" : (app.is_checked ? "#4B5563" : "#6B7280")} 
                      marginBottom={8}
                      style={{ fontWeight: 400 }}
                    >
                      Domains: {app.domains.map(d => d.name).join(', ')}
                    </Text>
                  )}
                  
                  {app.screenshot && app.screenshot.thumbnail_url && (
                    <Pane marginTop={8}>
                      <img 
                        src={app.screenshot.thumbnail_url} 
                        alt={`Screenshot of ${app.type === 'domain' ? app.name : (app.type === 'host' ? app.ip_address : app.url)}`}
                        onClick={() => setSelectedScreenshot(app.screenshot)}
                        style={{
                          maxWidth: '200px',
                          maxHeight: '120px',
                          borderRadius: '4px',
                          border: '1px solid #E6E8F0',
                          cursor: 'pointer',
                          objectFit: 'contain'
                        }}
                      />
                    </Pane>
                  )}
                </Pane>
                
                <Pane display="flex" flexDirection="column" gap={8}>
                  <Button
                    size="small"
                    appearance="minimal"
                    iconBefore={ArrowRightIcon}
                    onClick={() => openEntityOverview(app.type, app.id, { 
                      url: app.type === 'domain' ? `https://${app.name}` : (app.type === 'host' ? app.ip_address : app.url),
                      name: app.type === 'domain' ? app.name : (app.type === 'host' ? app.ip_address : app.url),
                      ip_address: app.type === 'host' ? app.ip_address : undefined,
                      type: app.type
                    })}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    iconBefore={CameraIcon}
                    isLoading={actionLoading[`screenshot_${app.type}_${app.id}`]}
                    onClick={() => handleAction('screenshot', app.type, app.id, {
                      url: app.type === 'domain' ? `https://${app.name}` : app.url,
                      name: app.type === 'domain' ? app.name : app.url
                    })}
                  >
                    Take Screenshot
                  </Button>
                  <Button
                    size="small"
                    iconBefore={PlayIcon}
                    isLoading={actionLoading[`nuclei_${app.type}_${app.id}`]}
                    onClick={() => handleAction('nuclei', app.type, app.id, {
                      url: app.type === 'domain' ? `https://${app.name}` : app.url,
                      name: app.type === 'domain' ? app.name : app.url
                    })}
                  >
                    Run Nuclei
                  </Button>
                  <Button
                    size="small"
                    iconBefore={TickIcon}
                    intent={app.is_checked ? "success" : "default"}
                    isLoading={actionLoading[`mark_checked_${app.type}_${app.id}`]}
                    onClick={() => handleAction('mark_checked', app.type, app.id, {
                      url: app.type === 'domain' ? `https://${app.name}` : app.url,
                      name: app.type === 'domain' ? app.name : app.url
                    })}
                  >
                    {app.is_checked ? 'Uncheck' : 'Mark Checked'}
                  </Button>
                  <IconButton
                    icon={TrashIcon}
                    intent="danger"
                    size="small"
                    appearance="minimal"
                    isLoading={actionLoading[`delete_${app.type}_${app.id}`]}
                    onClick={() => {
                      const label = app.type === 'domain' ? app.name : app.url;
                      if (window.confirm(`Delete ${label}?`)) {
                        handleAction('delete', app.type, app.id);
                      }
                    }}
                  />
                </Pane>
              </Pane>
            </Card>
          ))}
          
          {isLoading && (
            <Pane display="flex" justifyContent="center" padding={16}>
              <Spinner />
            </Pane>
          )}
        </Pane>
      )}
      
      {selectedScreenshot && (
        <Dialog
          isShown={!!selectedScreenshot}
          title="Screenshot"
          onCloseComplete={() => setSelectedScreenshot(null)}
          hasFooter={false}
          width="90vw"
          maxWidth="1200px"
        >
          <Pane padding={16}>
            <Image
              src={selectedScreenshot.screenshot_url || selectedScreenshot.thumbnail_url}
              alt="Full screenshot"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          </Pane>
        </Dialog>
      )}
    </Pane>
  );
};

